import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, MessageSquare, Baby, Phone, Loader2, Mail, Clock, Link2, UserCheck, UserX, Users, Download, Eye, Receipt, Filter, Send, Bell, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { exportToCSV } from "@/lib/export-utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import SendMessageDialog from "@/components/dialogs/SendMessageDialog";
import CreateParentAccountDialog from "@/components/dialogs/CreateParentAccountDialog";
import LinkChildrenDialog from "@/components/dialogs/LinkChildrenDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import ResetPasswordDialog from "@/components/dialogs/ResetPasswordDialog";
import EditParentDialog from "@/components/dialogs/EditParentDialog";
import { toast } from "sonner";

type PaymentStatus = "paid" | "overdue" | "pending" | "none";

const Parents = () => {
  const { profile, session } = useAuth();
  const nurseryId = profile?.nursery_id;
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [linkStatusFilter, setLinkStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [messageFilter, setMessageFilter] = useState("all");
  const [linkingParent, setLinkingParent] = useState<{ id: string; name: string } | null>(null);
  const [detailParent, setDetailParent] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: classes } = useQuery({
    queryKey: ["classes-list", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: children, isLoading, refetch: refetchChildren } = useQuery({
    queryKey: ["parents-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("children")
        .select("id, full_name, parent_id, parent_name, parent_phone, class_id, classes(name)")
        .eq("nursery_id", nurseryId!)
        .order("parent_name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: parentProfiles } = useQuery({
    queryKey: ["parent-profiles", nurseryId],
    queryFn: async () => {
      const { data: roles } = await supabase.from("user_roles").select("user_id").eq("role", "parent");
      if (!roles || roles.length === 0) return [];
      const parentUserIds = roles.map((r) => r.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name, phone, created_at")
        .eq("nursery_id", nurseryId!)
        .in("user_id", parentUserIds);
      return profiles || [];
    },
    enabled: !!nurseryId,
  });

  const { data: invoices } = useQuery({
    queryKey: ["parent-invoices", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("invoices")
        .select("id, child_id, title, amount, status, due_date")
        .eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: messages, refetch: refetchMessages } = useQuery({
    queryKey: ["sent-messages", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("messages")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false })
        .limit(50);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Compute payment status per parent
  const getParentPaymentStatus = (childIds: string[]): PaymentStatus => {
    if (childIds.length === 0) return "none";
    const parentInvoices = (invoices || []).filter((inv) => childIds.includes(inv.child_id));
    if (parentInvoices.length === 0) return "none";
    if (parentInvoices.some((inv) => inv.status === "overdue")) return "overdue";
    if (parentInvoices.some((inv) => inv.status === "pending" || inv.status === "pending_review" || inv.status === "bank_transfer")) return "pending";
    return "paid";
  };

  const parentList = useMemo(() => (parentProfiles || []).map((p) => {
    const linkedChildren = (children || []).filter((c) => c.parent_id === p.user_id);
    const childIds = linkedChildren.map((c) => c.id);
    return {
      userId: p.user_id,
      name: p.full_name || "بدون اسم",
      phone: p.phone,
      hasAccount: true,
      childrenCount: linkedChildren.length,
      children: linkedChildren.map((c) => ({
        id: c.id,
        name: c.full_name,
        className: (c as any).classes?.name || "-",
        classId: c.class_id,
      })),
      childIds,
      paymentStatus: getParentPaymentStatus(childIds),
      createdAt: p.created_at,
    };
  }), [parentProfiles, children, invoices]);

  const messageCounts = useMemo(() => (messages || []).reduce<Record<string, number>>((acc, m) => {
    if (m.receiver_id) acc[m.receiver_id] = (acc[m.receiver_id] || 0) + 1;
    return acc;
  }, {}), [messages]);

  // Apply filters
  const filteredParentList = parentList.filter((p) => {
    if (search && !p.name.includes(search) && !p.phone?.includes(search)) return false;
    if (classFilter !== "all" && !p.children.some((c) => c.classId === classFilter)) return false;
    if (linkStatusFilter === "linked" && p.childrenCount === 0) return false;
    if (linkStatusFilter === "unlinked" && p.childrenCount > 0) return false;
    if (paymentFilter !== "all" && p.paymentStatus !== paymentFilter) return false;
    return true;
  });

  const unlinkedChildren = (children || []).filter((c) => !c.parent_id);
  const totalParents = parentProfiles?.length || 0;
  const linkedParents = parentList.filter((p) => p.childrenCount > 0).length;
  const unlinkedParents = parentList.filter((p) => p.childrenCount === 0).length;
  const overdueParents = parentList.filter((p) => p.paymentStatus === "overdue").length;

  const parentNameMap = parentList.reduce<Record<string, string>>((acc, p) => {
    acc[p.userId] = p.name;
    return acc;
  }, {});

  const filteredMessages = (messages || []).filter((m) => {
    if (messageFilter === "read") return m.is_read;
    if (messageFilter === "unread") return !m.is_read;
    return true;
  });

  const formatDate = (d: string) => {
    try { return format(new Date(d), "d MMM yyyy - HH:mm", { locale: ar }); } catch { return d; }
  };

  const handleRefresh = () => {
    refetchChildren();
    queryClient.invalidateQueries({ queryKey: ["parent-profiles"] });
    queryClient.invalidateQueries({ queryKey: ["parent-invoices"] });
  };

  const exportToExcel = (ids?: string[]) => {
    const list = ids ? parentList.filter((p) => ids.includes(p.userId)) : parentList;
    const rows = list.map((p) => ({
      "اسم ولي الأمر": p.name,
      "رقم الهاتف": p.phone || "-",
      "عدد الأطفال": p.childrenCount,
      "أسماء الأطفال": p.children.map((c) => c.name).join("، ") || "-",
      "الفصول": [...new Set(p.children.map((c) => c.className))].join("، ") || "-",
      "حالة الدفع": p.paymentStatus === "paid" ? "مدفوع" : p.paymentStatus === "overdue" ? "متأخر" : p.paymentStatus === "pending" ? "معلق" : "-",
      "تاريخ الإنشاء": p.createdAt ? format(new Date(p.createdAt), "yyyy-MM-dd") : "-",
    }));
    exportToCSV(rows, "parents-report.csv");
  };

  // Bulk actions
  const allFilteredSelected = filteredParentList.length > 0 && filteredParentList.every((p) => selectedIds.includes(p.userId));
  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredParentList.map((p) => p.userId));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleBulkMessage = async () => {
    if (!nurseryId || !session?.user?.id) return;
    const msgContent = "تذكير بدفع الرسوم المستحقة. يرجى مراجعة الفواتير المعلقة.";
    const msgs = selectedIds.map((rid) => ({
      content: msgContent,
      sender_id: session.user.id,
      receiver_id: rid,
      nursery_id: nurseryId,
    }));
    const { error } = await supabase.from("messages").insert(msgs);
    if (error) {
      toast.error("خطأ في الإرسال: " + error.message);
    } else {
      toast.success(`تم إرسال تذكير إلى ${msgs.length} ولي أمر`);
      refetchMessages();
      setSelectedIds([]);
    }
  };

  const paymentStatusBadge = (status: PaymentStatus) => {
    switch (status) {
      case "paid": return <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/10 text-[10px]">مدفوع ✅</Badge>;
      case "overdue": return <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10 text-[10px]">متأخر ❌</Badge>;
      case "pending": return <Badge className="bg-amber-500/10 text-amber-600 hover:bg-amber-500/10 text-[10px]">معلق ⏳</Badge>;
      default: return <span className="text-xs text-muted-foreground">-</span>;
    }
  };

  // Detail
  const detailData = detailParent ? parentList.find((p) => p.userId === detailParent) : null;
  const detailInvoices = detailData ? (invoices || []).filter((inv) => detailData.childIds.includes(inv.child_id)) : [];
  const detailMessages = detailData ? (messages || []).filter((m) => m.receiver_id === detailData.userId).slice(0, 5) : [];
  const detailFinancial = useMemo(() => {
    if (!detailInvoices.length) return null;
    const paid = detailInvoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);
    const pending = detailInvoices.filter((i) => i.status === "pending" || i.status === "pending_review" || i.status === "bank_transfer").reduce((s, i) => s + (i.amount || 0), 0);
    const overdue = detailInvoices.filter((i) => i.status === "overdue").reduce((s, i) => s + (i.amount || 0), 0);
    return { paid, pending, overdue };
  }, [detailInvoices]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">بوابة أولياء الأمور</h1>
          <p className="text-muted-foreground text-sm mt-1">إدارة حسابات أولياء الأمور وربطهم بالأطفال</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {nurseryId && (
            <>
              <CreateParentAccountDialog
                children={(children || []).map((c) => ({ id: c.id, full_name: c.full_name, parent_id: c.parent_id }))}
                onSuccess={handleRefresh}
              />
              <SendMessageDialog
                parents={(children || []).map((c) => ({ parent_id: c.parent_id, parent_name: c.parent_name }))}
                nurseryId={nurseryId}
                onSuccess={refetchMessages}
              />
              <Button variant="outline" onClick={() => exportToExcel()} disabled={!parentProfiles?.length}>
                <Download className="w-4 h-4 ml-2" />
                تصدير Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "أولياء الأمور", value: totalParents, icon: Users, color: "text-primary", bg: "bg-primary/10" },
          { label: "مرتبطين بأطفال", value: linkedParents, icon: UserCheck, color: "text-emerald-500", bg: "bg-emerald-500/10" },
          { label: "غير مرتبطين", value: unlinkedParents, icon: UserX, color: unlinkedParents > 0 ? "text-amber-500" : "text-emerald-500", bg: unlinkedParents > 0 ? "bg-amber-500/10" : "bg-emerald-500/10" },
          { label: "متأخرين في الدفع", value: overdueParents, icon: AlertTriangle, color: overdueParents > 0 ? "text-destructive" : "text-emerald-500", bg: overdueParents > 0 ? "bg-destructive/10" : "bg-emerald-500/10" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="shadow-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${s.bg}`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <div className="text-xl font-bold text-foreground">{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="parents" dir="rtl">
        <TabsList>
          <TabsTrigger value="parents">حسابات أولياء الأمور ({totalParents})</TabsTrigger>
          <TabsTrigger value="unlinked">أطفال غير مرتبطين ({unlinkedChildren.length})</TabsTrigger>
          <TabsTrigger value="messages">الرسائل ({(messages || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="parents" className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="البحث بالاسم أو الهاتف..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="w-[150px] h-10">
                <Filter className="w-3.5 h-3.5 ml-1.5 text-muted-foreground" />
                <SelectValue placeholder="الفصل" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">كل الفصول</SelectItem>
                {(classes || []).map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={linkStatusFilter} onValueChange={setLinkStatusFilter}>
              <SelectTrigger className="w-[140px] h-10">
                <SelectValue placeholder="حالة الربط" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="linked">لديه أطفال</SelectItem>
                <SelectItem value="unlinked">بدون أطفال</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-[140px] h-10">
                <Receipt className="w-3.5 h-3.5 ml-1.5 text-muted-foreground" />
                <SelectValue placeholder="الدفع" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">كل الحالات</SelectItem>
                <SelectItem value="paid">مدفوع</SelectItem>
                <SelectItem value="overdue">متأخر</SelectItem>
                <SelectItem value="pending">معلق</SelectItem>
                <SelectItem value="none">بدون فواتير</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions Bar */}
          {selectedIds.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-medium text-primary">{selectedIds.length} محدد</span>
              <div className="flex gap-2 mr-auto">
                <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={handleBulkMessage}>
                  <Bell className="w-3.5 h-3.5" />
                  تذكير بالدفع
                </Button>
                <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => exportToExcel(selectedIds)}>
                  <Download className="w-3.5 h-3.5" />
                  تصدير المحدد
                </Button>
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => setSelectedIds([])}>
                  إلغاء التحديد
                </Button>
              </div>
            </motion.div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filteredParentList.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-16 text-center space-y-4">
                <Users className="w-14 h-14 text-muted-foreground/20 mx-auto" />
                <div>
                  <p className="text-muted-foreground font-medium">{search || classFilter !== "all" || paymentFilter !== "all" ? "لا توجد نتائج مطابقة للفلاتر" : "لا يوجد حسابات أولياء أمور بعد"}</p>
                  <p className="text-xs text-muted-foreground mt-1">ابدأ بإضافة حسابات أولياء الأمور وربطهم بالأطفال</p>
                </div>
                {!search && classFilter === "all" && nurseryId && (
                  <div className="flex justify-center gap-2">
                    <CreateParentAccountDialog
                      children={(children || []).map((c) => ({ id: c.id, full_name: c.full_name, parent_id: c.parent_id }))}
                      onSuccess={handleRefresh}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-10 text-center">
                          <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} />
                        </TableHead>
                        <TableHead className="text-right">#</TableHead>
                        <TableHead className="text-right">الاسم</TableHead>
                        <TableHead className="text-right">الهاتف</TableHead>
                        <TableHead className="text-right">الأطفال / الفصل</TableHead>
                        <TableHead className="text-right">حالة الدفع</TableHead>
                        <TableHead className="text-right">الرسائل</TableHead>
                        <TableHead className="text-right min-w-[220px]">إجراءات</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredParentList.map((p, i) => (
                        <TableRow key={p.userId} className="group">
                          <TableCell className="text-center">
                            <Checkbox checked={selectedIds.includes(p.userId)} onCheckedChange={() => toggleOne(p.userId)} />
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{p.name}</span>
                              <Badge variant="outline" className="text-[9px] text-primary border-primary/30 bg-primary/5 hidden sm:inline-flex">
                                مفعّل
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {p.phone ? <span dir="ltr" className="text-sm text-muted-foreground">{p.phone}</span> : <span className="text-xs text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell>
                            {p.children.length === 0 ? (
                              <span className="text-xs text-amber-500">لا يوجد أطفال</span>
                            ) : (
                              <div className="space-y-0.5">
                                {p.children.map((c, ci) => (
                                  <div key={ci} className="text-xs">
                                    <span className="text-foreground">{c.name}</span>
                                    <span className="text-muted-foreground/60 mr-1">({c.className})</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{paymentStatusBadge(p.paymentStatus)}</TableCell>
                          <TableCell>
                            {messageCounts[p.userId] ? (
                              <Badge variant="secondary" className="text-xs gap-1">
                                <Mail className="w-3 h-3" />
                                {messageCounts[p.userId]}
                              </Badge>
                            ) : <span className="text-xs text-muted-foreground">0</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="عرض" onClick={() => setDetailParent(p.userId)}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <EditParentDialog parentUserId={p.userId} currentName={p.name} currentPhone={p.phone} onSuccess={handleRefresh} />
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="ربط أطفال" onClick={() => setLinkingParent({ id: p.userId, name: p.name })}>
                                <Link2 className="w-3.5 h-3.5" />
                              </Button>
                              <ResetPasswordDialog parentUserId={p.userId} parentName={p.name} />
                              <DeleteConfirmDialog
                                title="حذف حساب ولي أمر"
                                description={`هل أنت متأكد من حذف حساب "${p.name}"؟ سيتم فك ربط جميع الأطفال المرتبطين.`}
                                onConfirm={async () => {
                                  const { data, error } = await supabase.functions.invoke("delete-parent-account", {
                                    body: { parent_user_id: p.userId, nursery_id: nurseryId },
                                  });
                                  if (error || data?.error) {
                                    toast.error("خطأ: " + (data?.error || error?.message));
                                  } else {
                                    toast.success("تم حذف حساب ولي الأمر بنجاح");
                                    handleRefresh();
                                  }
                                }}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Unlinked Children Tab */}
        <TabsContent value="unlinked" className="mt-4 space-y-4">
          {unlinkedChildren.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <UserCheck className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-muted-foreground">جميع الأطفال مرتبطين بحسابات أولياء أمور 🎉</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-right p-3 font-medium text-muted-foreground">الطفل</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">الفصل</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">اسم ولي الأمر</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">الهاتف</th>
                        <th className="text-right p-3 font-medium text-muted-foreground min-w-[200px]">ربط بولي أمر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unlinkedChildren.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="p-3 font-medium text-foreground">{c.full_name}</td>
                          <td className="p-3 text-muted-foreground">{(c as any).classes?.name || "-"}</td>
                          <td className="p-3 text-muted-foreground">{c.parent_name || "-"}</td>
                          <td className="p-3 text-muted-foreground" dir="ltr">{c.parent_phone || "-"}</td>
                          <td className="p-3">
                            {parentList.length > 0 ? (
                              <Select
                                onValueChange={async (parentUserId) => {
                                  const parent = parentList.find((p) => p.userId === parentUserId);
                                  const { error } = await supabase
                                    .from("children")
                                    .update({ parent_id: parentUserId, parent_name: parent?.name || null })
                                    .eq("id", c.id);
                                  if (error) {
                                    toast.error("فشل الربط: " + error.message);
                                  } else {
                                    toast.success(`تم ربط ${c.full_name} بـ ${parent?.name}`);
                                    handleRefresh();
                                  }
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="اختر ولي أمر" />
                                </SelectTrigger>
                                <SelectContent dir="rtl">
                                  {parentList.map((p) => (
                                    <SelectItem key={p.userId} value={p.userId} className="text-xs">
                                      {p.name} {p.phone ? `(${p.phone})` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-muted-foreground">لا يوجد أولياء أمور</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="mt-4 space-y-3">
          <div className="flex gap-2">
            {[
              { value: "all", label: "الكل" },
              { value: "unread", label: "غير مقروءة" },
              { value: "read", label: "مقروءة" },
            ].map((f) => (
              <Button
                key={f.value}
                variant={messageFilter === f.value ? "default" : "outline"}
                size="sm"
                className="text-xs"
                onClick={() => setMessageFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>

          {filteredMessages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">لا توجد رسائل</div>
          ) : (
            filteredMessages.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className="shadow-card">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div>
                          {m.receiver_id && parentNameMap[m.receiver_id] && (
                            <p className="text-xs font-medium text-primary mb-1">إلى: {parentNameMap[m.receiver_id]}</p>
                          )}
                          <p className="text-sm text-card-foreground">{m.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDate(m.created_at)}
                          </div>
                        </div>
                      </div>
                      {m.is_read ? (
                        <Badge variant="outline" className="text-xs shrink-0">مقروءة</Badge>
                      ) : (
                        <Badge className="text-xs shrink-0 bg-primary/10 text-primary hover:bg-primary/10">جديدة</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Link Children Dialog */}
      {linkingParent && nurseryId && (
        <LinkChildrenDialog
          parentId={linkingParent.id}
          parentName={linkingParent.name}
          nurseryId={nurseryId}
          allChildren={(children || []).map((c) => ({ id: c.id, full_name: c.full_name, parent_id: c.parent_id }))}
          open={!!linkingParent}
          onOpenChange={(open) => !open && setLinkingParent(null)}
          onSuccess={handleRefresh}
        />
      )}

      {/* Parent Detail Dialog */}
      <Dialog open={!!detailParent} onOpenChange={(open) => !open && setDetailParent(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              تفاصيل ولي الأمر
            </DialogTitle>
          </DialogHeader>
          {detailData && (
            <ScrollArea className="max-h-[70vh]">
              <div className="space-y-4 pl-2">
                {/* Basic Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{detailData.name}</span>
                    <Badge variant="outline" className="text-[10px] text-primary border-primary/30 bg-primary/5">حساب مفعّل</Badge>
                  </div>
                  {detailData.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-3.5 h-3.5" />
                      <span dir="ltr">{detailData.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    <span>تاريخ الإنشاء: {detailData.createdAt ? format(new Date(detailData.createdAt), "dd MMM yyyy", { locale: ar }) : "-"}</span>
                  </div>
                </div>

                {/* Financial Summary */}
                {detailFinancial && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-lg bg-emerald-500/10 text-center">
                      <div className="text-sm font-bold text-emerald-600">{detailFinancial.paid}</div>
                      <div className="text-[10px] text-emerald-600/70">مدفوع</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-amber-500/10 text-center">
                      <div className="text-sm font-bold text-amber-600">{detailFinancial.pending}</div>
                      <div className="text-[10px] text-amber-600/70">معلق</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-destructive/10 text-center">
                      <div className="text-sm font-bold text-destructive">{detailFinancial.overdue}</div>
                      <div className="text-[10px] text-destructive/70">متأخر</div>
                    </div>
                  </div>
                )}

                {/* Children */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Baby className="w-4 h-4 text-primary" />
                    الأطفال ({detailData.children.length})
                  </h4>
                  {detailData.children.length === 0 ? (
                    <p className="text-xs text-muted-foreground">لا يوجد أطفال مرتبطين</p>
                  ) : (
                    <div className="space-y-2">
                      {detailData.children.map((c) => (
                        <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                          <div>
                            <p className="text-sm font-medium text-foreground">{c.name}</p>
                            <p className="text-xs text-muted-foreground">الفصل: {c.className}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invoices */}
                {detailInvoices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                      <Receipt className="w-4 h-4 text-primary" />
                      الفواتير ({detailInvoices.length})
                    </h4>
                    <div className="space-y-1.5">
                      {detailInvoices.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-xs">
                          <span className="text-foreground">{inv.title}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">{inv.amount} ر.س</span>
                            <Badge variant={inv.status === "paid" ? "default" : "destructive"} className="text-[10px] px-1.5 py-0">
                              {inv.status === "paid" ? "مدفوعة" : inv.status === "overdue" ? "متأخرة" : "معلقة"}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Messages */}
                {detailMessages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-primary" />
                      آخر الرسائل
                    </h4>
                    <div className="space-y-1.5">
                      {detailMessages.map((m) => (
                        <div key={m.id} className="p-2 rounded-lg bg-muted/50 text-xs">
                          <p className="text-foreground line-clamp-2">{m.content}</p>
                          <p className="text-muted-foreground mt-1">{formatDate(m.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Parents;
